import { sortLabelledEntities } from 'helpers/sort';
import { CompanyModel } from 'shared/domain/company/types/model';
import { ContractModel } from 'shared/domain/contract/types/model';
import { EditableFieldValuesEntityName } from 'shared/domain/fieldValue/fields';
import { FieldValueModel } from 'shared/domain/fieldValue/fieldValueModel';
import { IssueFieldNames } from 'shared/domain/issueForm/types/fieldNames';
import { LevelModel } from 'shared/domain/level/types/model';
import { SiteModel } from 'shared/domain/site/types/model';
import { LabelledUser } from 'shared/domain/user/mapping/toView';
import { LabelledEntity } from 'shared/types/commonView';
import {
  FieldSkeleton,
  FormFieldType,
  IssueForm,
} from 'shared/types/form';
import type { FieldValueDictionaries } from 'store/fieldValue/types';
import { LegacyIssueForm } from './types';

export interface GenerateFormParams {
  issueForm: IssueForm;
  processWorkTypes: FieldValueModel[];
  hazardCategories: FieldValueModel[];
  environmentalAspects: FieldValueModel[];
  correctiveActionTypes: FieldValueModel[];
  issueVerificationItems: FieldValueModel[];
  rejectionReasons: FieldValueModel[];
  delayReasons: FieldValueModel[];
  rootCauses: FieldValueModel[];
  effects: FieldValueModel[];
  circumstances: FieldValueModel[];
  impacts: FieldValueModel[];
  priorities: FieldValueModel[];
  projectStages: FieldValueModel[];
  decisionsToImposeFine: FieldValueModel[];
  spilledSubstances: FieldValueModel[];
  employmentTypes: FieldValueModel[];
  injuryTypes: FieldValueModel[];
  injuredBodyParts: FieldValueModel[];
  requirements: FieldValueModel[];
  installationBasis: FieldValueModel[];
  constructionProductDocuments: FieldValueModel[];
  deviceDocuments: FieldValueModel[];
  additionalDocuments: FieldValueModel[];
  levels: LevelModel[];
  sites: SiteModel[];
  companies?: CompanyModel[];
  contracts?: ContractModel[];
  usersWithAccess?: LabelledUser[];
}

type FormFieldValues = Omit<
  GenerateFormParams,
  'issueForm' | 'levels' | 'sites' | 'companies' | 'contracts' | 'usersWithAccess'
>;

/** The dictionaries as the form generation names them. */
export function toFormFieldValues(dictionaries: FieldValueDictionaries): FormFieldValues {
  return {
    processWorkTypes: dictionaries[EditableFieldValuesEntityName.workTypes],
    hazardCategories: dictionaries[EditableFieldValuesEntityName.hazardCategory],
    environmentalAspects: dictionaries[EditableFieldValuesEntityName.environmentalAspect],
    correctiveActionTypes: dictionaries[EditableFieldValuesEntityName.proposedCorrectiveAction],
    issueVerificationItems: dictionaries[EditableFieldValuesEntityName.issueVerificationItem],
    rejectionReasons: dictionaries[EditableFieldValuesEntityName.rejectionReason],
    delayReasons: dictionaries[EditableFieldValuesEntityName.delayReason],
    rootCauses: dictionaries[EditableFieldValuesEntityName.rootCauses],
    effects: dictionaries[EditableFieldValuesEntityName.effect],
    circumstances: dictionaries[EditableFieldValuesEntityName.circumstances],
    impacts: dictionaries[EditableFieldValuesEntityName.impact],
    priorities: dictionaries[EditableFieldValuesEntityName.priority],
    projectStages: dictionaries[EditableFieldValuesEntityName.projectStage],
    decisionsToImposeFine: dictionaries[EditableFieldValuesEntityName.decisionToImposeFine],
    spilledSubstances: dictionaries[EditableFieldValuesEntityName.spilledSubstance],
    employmentTypes: dictionaries[EditableFieldValuesEntityName.employmentType],
    injuryTypes: dictionaries[EditableFieldValuesEntityName.injuryType],
    injuredBodyParts: dictionaries[EditableFieldValuesEntityName.injuredBodyPart],
    requirements: dictionaries[EditableFieldValuesEntityName.requirements],
    installationBasis: dictionaries[EditableFieldValuesEntityName.installationBasis],
    constructionProductDocuments: dictionaries[EditableFieldValuesEntityName.constructionProductDocuments],
    deviceDocuments: dictionaries[EditableFieldValuesEntityName.deviceDocuments],
    additionalDocuments: dictionaries[EditableFieldValuesEntityName.additionalDocuments],
  };
}

export type SubcontractorSelectItem = LabelledEntity & { sites: string[] };

export type ContractSelectItem = LabelledEntity & { partyCompanyIds: string[] };

export const mapCompaniesToSubcontractorSelectItems = (
  companies: CompanyModel[]
): SubcontractorSelectItem[] =>
  companies.map((company) => ({
    _id: company._id,
    label: company.shortLabel,
    sites: company.sites,
  }));

export const mapContractsToContractSelectItems = (
  contracts: ContractModel[]
): ContractSelectItem[] =>
  contracts.map((contract) => ({
    _id: contract._id,
    label: contract.label,
    partyCompanyIds: contract.parties.map((party) => party.companyId),
  }));

export const insertFieldValues = <T extends LabelledEntity>(
  fieldName: IssueFieldNames,
  extendedFields: FieldSkeleton[],
  values: T[]
): void => {
  const field = extendedFields.find((field) => field.name === fieldName);

  if (field) {
    field.items = values;
  }
};

export const sortFieldValues = <T extends FieldSkeleton>(
  fields: T[]
): T[] => {
  const affectedFields = [
    IssueFieldNames.proposedCorrectiveAction,
    IssueFieldNames.assignee,
    IssueFieldNames.site,
    IssueFieldNames.estimatedCost,
    IssueFieldNames.finalCost,
    IssueFieldNames.executor,
    IssueFieldNames.supervisor,
  ];

  return fields.map((field) => {
    const shouldSortField = affectedFields.includes(field.name);
    if (shouldSortField && Array.isArray(field.items)) {
      field.items.sort(sortLabelledEntities);
    }

    return field;
  });
};

export function generateForm(params: GenerateFormParams): LegacyIssueForm {
  const {
    issueForm,
    processWorkTypes,
    hazardCategories,
    environmentalAspects,
    correctiveActionTypes,
    issueVerificationItems,
    rejectionReasons,
    delayReasons,
    rootCauses,
    effects,
    circumstances,
    impacts,
    priorities,
    projectStages,
    decisionsToImposeFine,
    spilledSubstances,
    employmentTypes,
    injuryTypes,
    injuredBodyParts,
    requirements,
    installationBasis,
    constructionProductDocuments,
    deviceDocuments,
    additionalDocuments,
    levels,
    sites,
    companies,
    contracts,
    usersWithAccess,
  } = params;
  const filterUndefined = (elem: unknown): boolean => Boolean(elem);

  const { primaryFields, extendedFields } = separateFields(issueForm);
  const primaryFieldsWithoutDocuments =
    primaryFields &&
    primaryFields.filter(
      (prop) =>
        filterUndefined(prop) && prop.name !== IssueFieldNames.documents
    );

  insertFieldValues(
    IssueFieldNames.site,
    primaryFieldsWithoutDocuments,
    sites
  );

  insertFieldValues(
    IssueFieldNames.level,
    primaryFieldsWithoutDocuments,
    levels
  );

  if (companies) {
    insertFieldValues(
      IssueFieldNames.subcontractors,
      primaryFieldsWithoutDocuments,
      mapCompaniesToSubcontractorSelectItems(companies)
    );
  }

  if (contracts) {
    insertFieldValues(
      IssueFieldNames.contractNumbers,
      primaryFieldsWithoutDocuments,
      mapContractsToContractSelectItems(contracts)
    );
  }

  const filteredFields = extendedFields.filter(filterUndefined);
  insertFieldValues(
    IssueFieldNames.workTypes,
    filteredFields,
    processWorkTypes
  );
  insertFieldValues(
    IssueFieldNames.hazardCategory,
    filteredFields,
    hazardCategories
  );
  insertFieldValues(
    IssueFieldNames.environmentalAspect,
    filteredFields,
    environmentalAspects
  );
  insertFieldValues(
    IssueFieldNames.proposedCorrectiveAction,
    filteredFields,
    correctiveActionTypes
  );
  insertFieldValues(
    IssueFieldNames.issueVerification,
    filteredFields,
    issueVerificationItems
  );
  insertFieldValues(
    IssueFieldNames.rejectionReason,
    filteredFields,
    rejectionReasons
  );
  insertFieldValues(
    IssueFieldNames.delayReason,
    filteredFields,
    delayReasons
  );
  insertFieldValues(
    IssueFieldNames.rootCauses,
    filteredFields,
    rootCauses
  );
  insertFieldValues(IssueFieldNames.effect, filteredFields, effects);
  insertFieldValues(
    IssueFieldNames.circumstances,
    filteredFields,
    circumstances
  );
  insertFieldValues(IssueFieldNames.impact, filteredFields, impacts);
  insertFieldValues(IssueFieldNames.priority, filteredFields, priorities);
  insertFieldValues(
    IssueFieldNames.projectStage,
    filteredFields,
    projectStages
  );
  insertFieldValues(
    IssueFieldNames.decisionToImposeFine,
    filteredFields,
    decisionsToImposeFine
  );
  insertFieldValues(
    IssueFieldNames.spilledSubstance,
    filteredFields,
    spilledSubstances
  );
  insertFieldValues(
    IssueFieldNames.employmentType,
    filteredFields,
    employmentTypes
  );
  insertFieldValues(
    IssueFieldNames.injuryTypes,
    filteredFields,
    injuryTypes
  );
  insertFieldValues(
    IssueFieldNames.injuredBodyParts,
    filteredFields,
    injuredBodyParts
  );
  insertFieldValues(
    IssueFieldNames.requirements,
    filteredFields,
    requirements
  );
  insertFieldValues(
    IssueFieldNames.installationBasis,
    filteredFields,
    installationBasis
  );
  insertFieldValues(
    IssueFieldNames.constructionProductDocuments,
    filteredFields,
    constructionProductDocuments
  );
  insertFieldValues(
    IssueFieldNames.deviceDocuments,
    filteredFields,
    deviceDocuments
  );
  insertFieldValues(
    IssueFieldNames.additionalDocuments,
    filteredFields,
    additionalDocuments
  );
  if (usersWithAccess) {
    insertFieldValues(
      IssueFieldNames.assignee,
      primaryFieldsWithoutDocuments,
      usersWithAccess
    );
    insertFieldValues(
      IssueFieldNames.executor,
      primaryFieldsWithoutDocuments,
      usersWithAccess
    );
    insertFieldValues(
      IssueFieldNames.supervisor,
      filteredFields,
      usersWithAccess
    );
  }

  return {
    primaryFields: sortFieldValues(primaryFieldsWithoutDocuments),
    extendedFields: sortFieldValues(filteredFields),
  };
}

export function separateFields(form: IssueForm): LegacyIssueForm {
  return form.reduce<LegacyIssueForm>(
    (result, field?: FieldSkeleton) => {
      if (!field?.fieldType) return result;
      if (field.fieldType === FormFieldType.primary) {
        result.primaryFields.push(field);
      } else {
        result.extendedFields.push(field);
      }
      return result;
    },
    {
      primaryFields: [],
      extendedFields: [],
    } as LegacyIssueForm
  );
}
